import React, {
    FormEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "react-toastify";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Trip } from "../models/Trip";
import API_BASE_URL from "../config";
import TripSummaryCard from "./TripSummaryCard";
import SettlementSummaryCard from "./SettlementSummaryCard";
import "./TripDetails.css";

type ParticipantView = {
    id: string;
    name: string;
    email: string;
    participantDone?: boolean;
};

type ExpenseView = {
    id: string;
    description: string;
    amount: number;
    currency: string;
    participant: ParticipantView | null;
    sharedBy: Array<ParticipantView | string>;
};

type TripView = Omit<Trip, "participants" | "expenses"> & {
    id: string;
    name: string;
    participants: ParticipantView[];
    expenses: ExpenseView[];
};

type Settlement = {
    from: string;
    to: string;
    amount: number;
    currency: string;
};

type ContactSuggestion = {
    id: string;
    name: string;
    email: string;
};

const PAGE_SIZE = 20;


function formatAmount(value: number, currency: string): string {
    const normalizedCurrency = currency?.trim().toUpperCase() || "CAD";

    try {
        return Number(value).toLocaleString("en-CA", {
            style: "currency",
            currency: normalizedCurrency,
        });
    } catch {
        return `${normalizedCurrency} ${Number(value).toFixed(2)}`;
    }
}

function getInitials(name: string, email: string): string {
    const source = name.trim() || email.trim();

    if (!source) {
        return "?";
    }

    const parts = source
        .split(/[\s@._-]+/)
        .map((part) => part.trim())
        .filter(Boolean);

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizeParticipant(value: any): ParticipantView {
    return {
        id: String(value?.id ?? value?.Id ?? ""),
        name: String(value?.name ?? value?.Name ?? ""),
        email: String(value?.email ?? value?.Email ?? ""),
        participantDone: Boolean(
            value?.participantDone ?? value?.ParticipantDone ?? false
        ),
    };
}

function normalizeExpense(value: any): ExpenseView {
    const paidBy =
        value?.participant ??
        value?.Participant ??
        value?.paidBy ??
        value?.PaidBy;

    const sharedBy = value?.sharedBy ?? value?.SharedBy ?? [];

    return {
        id: String(value?.id ?? value?.Id ?? ""),
        description: String(
            value?.description ?? value?.Description ?? ""
        ),
        amount: Number(value?.amount ?? value?.Amount ?? 0),
        currency: String(
            value?.currency ?? value?.Currency ?? "CAD"
        ).toUpperCase(),
        participant: paidBy ? normalizeParticipant(paidBy) : null,
        sharedBy: Array.isArray(sharedBy)
            ? sharedBy.map((item: any) =>
                  typeof item === "string"
                      ? item
                      : normalizeParticipant(item)
              )
            : [],
    };
}

function normalizeTrip(value: any): TripView {
    const participants =
        value?.participants ?? value?.Participants ?? [];

    const expenses =
        value?.expenses ?? value?.Expenses ?? [];

    return {
        ...value,
        id: String(value?.id ?? value?.Id ?? ""),
        name: String(value?.name ?? value?.Name ?? ""),
        participants: Array.isArray(participants)
            ? participants.map(normalizeParticipant)
            : [],
        expenses: Array.isArray(expenses)
            ? expenses.map(normalizeExpense)
            : [],
    } as TripView;
}

async function readResponseBody(response: Response): Promise<any> {
    const contentType =
        response.headers.get("content-type") ?? "";

    if (!contentType.includes("application/json")) {
        return null;
    }

    return response.json();
}

/*
 * Calculate settlements independently for each currency.
 *
 * Example:
 *
 * CAD:
 *   Alice -> Bob  $20 CAD
 *
 * USD:
 *   Bob -> Alice  $15 USD
 *
 * No currency is ever converted or combined with another currency.
 */

function calculateSettlements(trip: TripView): Settlement[] {
    const balancesByCurrency = new Map<
        string,
        Map<string, number>
    >();

    for (const expense of trip.expenses) {
        const currency =
            expense.currency?.trim().toUpperCase() || "CAD";

        const amount = Number(expense.amount || 0);

        if (!expense.participant || amount <= 0) {
            continue;
        }

        let balances = balancesByCurrency.get(currency);

        if (!balances) {
            balances = new Map<string, number>();

            trip.participants.forEach((participant) => {
                balances!.set(participant.id, 0);
            });

            balancesByCurrency.set(currency, balances);
        }

        const payerId = expense.participant.id;

        if (!balances.has(payerId)) {
            balances.set(payerId, 0);
        }

        // Credit the person who paid.
        balances.set(
            payerId,
            (balances.get(payerId) || 0) + amount
        );

        if (expense.sharedBy.length === 0) {
            continue;
        }

        const sharePerPerson =
            amount / expense.sharedBy.length;

        // Debit each person sharing the expense.
        expense.sharedBy.forEach((shared) => {
            const sharedId =
                typeof shared === "string"
                    ? shared
                    : shared.id;

            if (!sharedId) {
                return;
            }

            if (!balances!.has(sharedId)) {
                balances!.set(sharedId, 0);
            }

            balances!.set(
                sharedId,
                (balances!.get(sharedId) || 0) -
                    sharePerPerson
            );
        });
    }

    const settlements: Settlement[] = [];

    /*
     * Do NOT use:
     *
     * for (const [currency, balances] of balancesByCurrency)
     *
     * because the project targets an older JavaScript version.
     *
     * forEach works with the existing TypeScript configuration.
     */
    balancesByCurrency.forEach(
        (balances, currency) => {
            type BalanceEntry = {
                participantId: string;
                amount: number;
            };

            const positive: BalanceEntry[] = [];

            const negative: BalanceEntry[] = [];

            balances.forEach(
                (balance, participantId) => {
                    const rounded =
                        Math.round(
                            balance * 100
                        ) / 100;

                    if (rounded > 0.009) {
                        positive.push({
                            participantId,
                            amount: rounded,
                        });
                    } else if (
                        rounded < -0.009
                    ) {
                        negative.push({
                            participantId,
                            amount:
                                Math.round(
                                    -rounded * 100
                                ) / 100,
                        });
                    }
                }
            );

            positive.sort(
                (a, b) => b.amount - a.amount
            );

            negative.sort(
                (a, b) => b.amount - a.amount
            );

            let i = 0;
            let j = 0;

            while (
                i < negative.length &&
                j < positive.length
            ) {
                const payer = negative[i];
                const receiver = positive[j];

                const amount =
                    Math.round(
                        Math.min(
                            payer.amount,
                            receiver.amount
                        ) * 100
                    ) / 100;

                if (amount > 0) {
                    const payerParticipant =
                        trip.participants.find(
                            (participant) =>
                                participant.id ===
                                payer.participantId
                        );

                    const receiverParticipant =
                        trip.participants.find(
                            (participant) =>
                                participant.id ===
                                receiver.participantId
                        );

                    if (
                        payerParticipant &&
                        receiverParticipant
                    ) {
                        settlements.push({
                            from:
                                payerParticipant.name,
                            to:
                                receiverParticipant.name,
                            amount,
                            currency,
                        });
                    }

                    payer.amount =
                        Math.round(
                            (payer.amount -
                                amount) *
                                100
                        ) / 100;

                    receiver.amount =
                        Math.round(
                            (receiver.amount -
                                amount) *
                                100
                        ) / 100;
                }

                if (payer.amount < 0.01) {
                    i++;
                }

                if (receiver.amount < 0.01) {
                    j++;
                }
            }
        }
    );

    return settlements;
}


function TripDetails() {
    const { id } = useParams<{ id: string }>();
    const {
        getAccessTokenSilently,
        user,
    } = useAuth0();

    const [trip, setTrip] =
        useState<TripView | null>(null);

    const [isLoading, setIsLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [
        showAddParticipant,
        setShowAddParticipant,
    ] = useState(false);

    const [participantName, setParticipantName] =
        useState("");

    const [participantEmail, setParticipantEmail] =
        useState("");

    const [
        addingParticipant,
        setAddingParticipant,
    ] = useState(false);

    const [
        participantErrorMsg,
        setParticipantErrorMsg,
    ] = useState("");

    const [
        showAddExpense,
        setShowAddExpense,
    ] = useState(false);

    const [
        expenseDescription,
        setExpenseDescription,
    ] = useState("");

    const [expenseAmount, setExpenseAmount] =
        useState<number>(0);

    const [
        expenseCurrency,
        setExpenseCurrency,
    ] = useState("CAD");

    const [
        paidByParticipantId,
        setPaidByParticipantId,
    ] = useState("");

    const [
        sharedByParticipantIds,
        setSharedByParticipantIds,
    ] = useState<string[]>([]);

    const [
        addingExpense,
        setAddingExpense,
    ] = useState(false);

    const [expenseError, setExpenseError] =
        useState("");

    const [marking, setMarking] =
        useState(false);

    const [markError, setMarkError] =
        useState("");

    const [
        markStatusMessage,
        setMarkStatusMessage,
    ] = useState("");

    const [emailing, setEmailing] =
        useState(false);

    const [emailResult, setEmailResult] =
        useState("");

    const [emailError, setEmailError] =
        useState("");

    const [notifyLoading, setNotifyLoading] =
        useState(false);

    const [
        contactSuggestions,
        setContactSuggestions,
    ] = useState<ContactSuggestion[]>([]);

    const [
        contactSearchLoading,
        setContactSearchLoading,
    ] = useState(false);

    const contactSearchTimer =
        useRef<ReturnType<typeof setTimeout> | null>(
            null
        );

    const [
        itemSuggestions,
        setItemSuggestions,
    ] = useState<any[]>([]);

    const itemSearchTimer =
        useRef<ReturnType<typeof setTimeout> | null>(
            null
        );

    const [expensesPage, setExpensesPage] =
        useState(1);

    const [expensesTotal, setExpensesTotal] =
        useState<number | null>(null);

    const [loadingExpenses, setLoadingExpenses] =
        useState(false);

    const getToken = useCallback(
        () => getAccessTokenSilently(),
        [getAccessTokenSilently]
    );

    const fetchExpensePage = useCallback(
        async (
            page: number
        ): Promise<{
            items: ExpenseView[];
            total: number | null;
        }> => {
            if (!id) {
                return {
                    items: [],
                    total: null,
                };
            }

            const token = await getToken();

            const response = await fetch(
                `${API_BASE_URL}/trips/${id}/expenses?page=${page}&pageSize=${PAGE_SIZE}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch expenses (${response.status})`
                );
            }

            const data =
                await readResponseBody(response);

            const rawItems =
                data?.items ??
                data?.Items ??
                [];

            const rawTotal =
                data?.total ??
                data?.Total;

            return {
                items: Array.isArray(rawItems)
                    ? rawItems.map(normalizeExpense)
                    : [],

                total:
                    rawTotal === undefined ||
                    rawTotal === null
                        ? null
                        : Number(rawTotal),
            };
        },
        [getToken, id]
    );

    const fetchTrip = useCallback(
        async () => {
            if (!id) {
                setTrip(null);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setPageError("");

            try {
                const token =
                    await getToken();

                let response = await fetch(
                    `${API_BASE_URL}/trips/${id}/summary`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.status === 404) {
                    response = await fetch(
                        `${API_BASE_URL}/trips/${id}`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                }

                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch trip (${response.status})`
                    );
                }

                const data =
                    await readResponseBody(response);

                const normalized =
                    normalizeTrip(data);

                if (
                    normalized.expenses.length ===
                    0
                ) {
                    try {
                        const firstPage =
                            await fetchExpensePage(
                                1
                            );

                        normalized.expenses =
                            firstPage.items;

                        setExpensesTotal(
                            firstPage.total
                        );

                        setExpensesPage(1);
                    } catch (
                        expenseLoadError
                    ) {
                        console.error(
                            expenseLoadError
                        );

                        setExpensesTotal(null);
                    }
                } else {
                    setExpensesTotal(
                        normalized.expenses.length
                    );

                    setExpensesPage(1);
                }

                setTrip(normalized);
            } catch (error: any) {
                console.error(error);

                setTrip(null);

                setPageError(
                    error?.message ??
                        "Unable to load this trip."
                );
            } finally {
                setIsLoading(false);
            }
        },
        [fetchExpensePage, getToken, id]
    );

    useEffect(() => {
        void fetchTrip();
    }, [fetchTrip]);

    useEffect(() => {
        return () => {
            if (
                contactSearchTimer.current
            ) {
                clearTimeout(
                    contactSearchTimer.current
                );
            }

            if (
                itemSearchTimer.current
            ) {
                clearTimeout(
                    itemSearchTimer.current
                );
            }
        };
    }, []);

    const loadMoreExpenses = async () => {
        const nextPage =
            expensesPage + 1;

        setLoadingExpenses(true);

        try {
            const next =
                await fetchExpensePage(
                    nextPage
                );

            setExpensesTotal(
                next.total
            );

            setExpensesPage(
                nextPage
            );

            setTrip((previous) => {
                if (!previous) {
                    return previous;
                }

                const existingIds =
                    new Set(
                        previous.expenses.map(
                            (expense) =>
                                expense.id
                        )
                    );

                const newItems =
                    next.items.filter(
                        (expense) =>
                            !existingIds.has(
                                expense.id
                            )
                    );

                return {
                    ...previous,
                    expenses: [
                        ...previous.expenses,
                        ...newItems,
                    ],
                };
            });
        } catch (error: any) {
            console.error(error);

            toast.error(
                error?.message ??
                    "Failed to load more expenses."
            );
        } finally {
            setLoadingExpenses(false);
        }
    };

    const searchContacts = async (
        query: string
    ) => {
        const trimmed =
            query.trim();

        if (trimmed.length < 2) {
            setContactSuggestions([]);
            return;
        }

        try {
            setContactSearchLoading(true);

            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/contacts?search=${encodeURIComponent(
                        trimmed
                    )}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

            if (!response.ok) {
                throw new Error(
                    "Failed to search contacts."
                );
            }

            const data =
                await readResponseBody(
                    response
                );

            const values =
                Array.isArray(data)
                    ? data
                    : [];

            setContactSuggestions(
                values.map(
                    (item: any) => ({
                        id: String(
                            item?.id ??
                                item?.Id ??
                                item?.email ??
                                item?.Email ??
                                ""
                        ),
                        name: String(
                            item?.name ??
                                item?.Name ??
                                ""
                        ),
                        email: String(
                            item?.email ??
                                item?.Email ??
                                ""
                        ),
                    })
                )
            );
        } catch (error) {
            console.error(error);
            setContactSuggestions([]);
        } finally {
            setContactSearchLoading(
                false
            );
        }
    };

    const handleParticipantSearchChange =
        (value: string) => {
            setParticipantEmail(value);

            if (
                contactSearchTimer.current
            ) {
                clearTimeout(
                    contactSearchTimer.current
                );
            }

            contactSearchTimer.current =
                setTimeout(() => {
                    void searchContacts(
                        value
                    );
                }, 300);
        };
        const handleParticipantNameSearchChange =
        (value: string) => {
            setParticipantName(value);

            if (
                contactSearchTimer.current
            ) {
                clearTimeout(
                    contactSearchTimer.current
                );
            }

            contactSearchTimer.current =
                setTimeout(() => {
                    void searchContacts(
                        value
                    );
                }, 300);
        };
    const openAddParticipantModal =
        () => {
            setParticipantName("");
            setParticipantEmail("");
            setParticipantErrorMsg("");
            setContactSuggestions([]);
            setShowAddParticipant(true);
        };

    const closeAddParticipantModal =
        () => {
            setShowAddParticipant(false);
            setContactSuggestions([]);
        };

    const handleAddParticipant = async (
        event: FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        setParticipantErrorMsg("");

        if (
            !id ||
            !participantName.trim() ||
            !participantEmail.trim()
        ) {
            setParticipantErrorMsg(
                "Name and email are required."
            );

            return;
        }

        setAddingParticipant(true);

        try {
            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/trips/${id}/participants`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            name:
                                participantName.trim(),
                            email:
                                participantEmail.trim(),
                        }),
                    }
                );

            const data =
                await readResponseBody(
                    response
                );

            if (!response.ok) {
                throw new Error(
                    data?.message ??
                        "Failed to add participant."
                );
            }

            closeAddParticipantModal();

            await fetchTrip();

            toast.success(
                "Participant added."
            );
        } catch (error: any) {
            console.error(error);

            setParticipantErrorMsg(
                error?.message ??
                    "Unable to add participant."
            );
        } finally {
            setAddingParticipant(false);
        }
    };

    const handleDeleteParticipant =
        async (
            participantId: string
        ) => {
            if (
                !id ||
                !window.confirm(
                    "Delete this participant?"
                )
            ) {
                return;
            }

            try {
                const token =
                    await getToken();

                const response =
                    await fetch(
                        `${API_BASE_URL}/trips/${id}/participants/${participantId}`,
                        {
                            method: "DELETE",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                const data =
                    await readResponseBody(
                        response
                    );

                if (!response.ok) {
                    throw new Error(
                        data?.message ??
                            "Failed to delete participant."
                    );
                }

                await fetchTrip();

                toast.success(
                    "Participant deleted."
                );
            } catch (error: any) {
                console.error(error);

                toast.error(
                    error?.message ??
                        "Failed to delete participant."
                );
            }
        };

    const openAddExpenseModal = () => {
        setExpenseDescription("");
        setExpenseAmount(0);
        setExpenseCurrency("CAD");
        setPaidByParticipantId("");
        setSharedByParticipantIds([]);
        setItemSuggestions([]);
        setExpenseError("");
        setShowAddExpense(true);
    };

    const closeAddExpenseModal = () => {
        setShowAddExpense(false);
    };

    const searchSavedItems = async (
        query: string
    ) => {
        const trimmed =
            query.trim();

        if (trimmed.length < 1) {
            setItemSuggestions([]);
            return;
        }

        try {
            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/saveditems?q=${encodeURIComponent(
                        trimmed
                    )}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

            if (!response.ok) {
                setItemSuggestions([]);
                return;
            }

            const data =
                await readResponseBody(
                    response
                );

            setItemSuggestions(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch (error) {
            console.error(error);
            setItemSuggestions([]);
        }
    };

    const handleExpenseDescriptionChange =
        (value: string) => {
            setExpenseDescription(value);

            if (
                itemSearchTimer.current
            ) {
                clearTimeout(
                    itemSearchTimer.current
                );
            }

            itemSearchTimer.current =
                setTimeout(() => {
                    void searchSavedItems(
                        value
                    );
                }, 250);
        };

    const toggleSharedBy = (
        participantId: string
    ) => {
        setSharedByParticipantIds(
            (previous) =>
                previous.includes(
                    participantId
                )
                    ? previous.filter(
                          (idValue) =>
                              idValue !==
                              participantId
                      )
                    : [
                          ...previous,
                          participantId,
                      ]
        );
    };

    const handleAddExpense = async (
        event: FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        setExpenseError("");

        if (
            !id ||
            !expenseDescription.trim() ||
            expenseAmount <= 0 ||
            !paidByParticipantId
        ) {
            setExpenseError(
                "Enter a description, a positive amount, and the participant who paid."
            );

            return;
        }

        if (
            sharedByParticipantIds.length ===
            0
        ) {
            setExpenseError(
                "Select at least one participant under Shared By."
            );

            return;
        }

        setAddingExpense(true);

        try {
            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/trips/${id}/expenses`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            description:
                                expenseDescription.trim(),
                            amount:
                                expenseAmount,
                            currency:
                                expenseCurrency
                                    .trim()
                                    .toUpperCase() ||
                                "CAD",
                            paidByParticipantId,
                            sharedByParticipantIds,
                        }),
                    }
                );

            const data =
                await readResponseBody(
                    response
                );

            if (!response.ok) {
                throw new Error(
                    data?.message ??
                        "Failed to add expense."
                );
            }

            try {
                await fetch(
                    `${API_BASE_URL}/saveditems`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            name:
                                expenseDescription.trim(),
                            defaultAmount:
                                expenseAmount,
                            currency:
                                expenseCurrency
                                    .trim()
                                    .toUpperCase() ||
                                "CAD",
                        }),
                    }
                );
            } catch (
                saveItemError
            ) {
                console.error(
                    "Failed to save item",
                    saveItemError
                );
            }

            closeAddExpenseModal();

            await fetchTrip();

            toast.success(
                "Expense added."
            );
        } catch (error: any) {
            console.error(error);

            setExpenseError(
                error?.message ??
                    "Unable to add expense."
            );
        } finally {
            setAddingExpense(false);
        }
    };

    const handleDeleteExpense =
        async (
            expenseId: string
        ) => {
            if (
                !id ||
                !window.confirm(
                    "Delete this expense?"
                )
            ) {
                return;
            }

            try {
                const token =
                    await getToken();

                const response =
                    await fetch(
                        `${API_BASE_URL}/trips/${id}/expenses/${expenseId}`,
                        {
                            method: "DELETE",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                const data =
                    await readResponseBody(
                        response
                    );

                if (!response.ok) {
                    throw new Error(
                        data?.message ??
                            "Failed to delete expense."
                    );
                }

                await fetchTrip();

                toast.success(
                    "Expense deleted."
                );
            } catch (error: any) {
                console.error(error);

                toast.error(
                    error?.message ??
                        "Failed to delete expense."
                );
            }
        };

    const markParticipantDone =
        async () => {
            if (
                !id ||
                !user?.email
            ) {
                setMarkError(
                    "Your Auth0 profile does not contain an email address."
                );

                return;
            }

            setMarkError("");
            setMarkStatusMessage("");
            setMarking(true);

            try {
                const currentParticipant =
                    trip?.participants.find(
                        (participant) =>
                            participant.email
                                .toLowerCase() ===
                            user.email?.toLowerCase()
                    );

                const token =
                    await getToken();

                const response =
                    await fetch(
                        `${API_BASE_URL}/trips/${id}/setparticipantdone`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type":
                                    "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                email:
                                    user.email,
                                markDone:
                                    !(
                                        currentParticipant?.participantDone ??
                                        false
                                    ),
                            }),
                        }
                    );

                const data =
                    await readResponseBody(
                        response
                    );

                if (!response.ok) {
                    throw new Error(
                        data?.message ??
                            "Failed to update participant status."
                    );
                }

                setMarkStatusMessage(
                    "Your status was updated."
                );

                await fetchTrip();

                window.setTimeout(
                    () => {
                        setMarkStatusMessage(
                            ""
                        );
                    },
                    3000
                );
            } catch (error: any) {
                console.error(error);

                setMarkError(
                    error?.message ??
                        "Unable to update your status."
                );
            } finally {
                setMarking(false);
            }
        };

    const sendTripByEmail = async () => {
        if (!id) {
            return;
        }

        setEmailError("");
        setEmailResult("");
        setEmailing(true);

        try {
            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/trips/${id}/emailtrip`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            fromEmail:
                                user?.email ??
                                null,
                        }),
                    }
                );

            const data =
                await readResponseBody(
                    response
                );

            if (!response.ok) {
                throw new Error(
                    data?.message ??
                        "Failed to send the trip email."
                );
            }

            const failures =
                Array.isArray(data)
                    ? data.filter(
                          (item: any) =>
                              item?.status ===
                              "error"
                      ).length
                    : 0;

            if (failures > 0) {
                setEmailResult(
                    `Email process completed with ${failures} failure(s).`
                );
            } else {
                setEmailResult(
                    "Trip details were emailed to the participants."
                );
            }
        } catch (error: any) {
            console.error(error);

            setEmailError(
                error?.message ??
                    "Unable to send trip emails."
            );
        } finally {
            setEmailing(false);
        }
    };

    const notifyParticipant = async (
        participant: ParticipantView
    ) => {
        if (!id) {
            return;
        }

        setEmailError("");
        setNotifyLoading(true);

        try {
            const token =
                await getToken();

            const response =
                await fetch(
                    `${API_BASE_URL}/trips/${id}/notifyparticipant`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            toEmail:
                                participant.email,
                            toName:
                                participant.name,
                            pageUrl:
                                window.location.href,
                            fromEmail:
                                user?.email ??
                                null,
                        }),
                    }
                );

            const data =
                await readResponseBody(
                    response
                );

            if (!response.ok) {
                throw new Error(
                    data?.message ??
                        "Failed to send notification."
                );
            }

            toast.success(
                `Notification sent to ${
                    participant.name ||
                    participant.email
                }.`
            );
        } catch (error: any) {
            console.error(error);

            setEmailError(
                error?.message ??
                    "Unable to send notification."
            );

            toast.error(
                `Failed to notify ${
                    participant.name ||
                    participant.email
                }.`
            );
        } finally {
            setNotifyLoading(false);
        }
    };

    const notifyAllParticipants =
        async () => {
            if (!id) {
                return;
            }

            setEmailError("");
            setNotifyLoading(true);

            try {
                const token =
                    await getToken();

                const response =
                    await fetch(
                        `${API_BASE_URL}/trips/${id}/notifyall`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type":
                                    "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                pageUrl:
                                    window.location.href,
                                fromEmail:
                                    user?.email ??
                                    null,
                            }),
                        }
                    );

                const data =
                    await readResponseBody(
                        response
                    );

                if (!response.ok) {
                    throw new Error(
                        data?.message ??
                            "Failed to notify all participants."
                    );
                }

                toast.success(
                    "Notifications sent to all participants."
                );
            } catch (error: any) {
                console.error(error);

                setEmailError(
                    error?.message ??
                        "Unable to notify all participants."
                );

                toast.error(
                    "Failed to notify all participants."
                );
            } finally {
                setNotifyLoading(false);
            }
        };

    const myParticipant =
        useMemo(() => {
            if (
                !trip ||
                !user?.email
            ) {
                return undefined;
            }

            return trip.participants.find(
                (participant) =>
                    participant.email
                        .toLowerCase() ===
                    user.email?.toLowerCase()
            );
        }, [trip, user?.email]);

    const expensesWithNames =
        useMemo(() => {
            if (!trip) {
                return [];
            }

            return trip.expenses.map(
                (expense) => {
                    const sharedByNames =
                        expense.sharedBy.length >
                        0
                            ? expense.sharedBy
                                  .map(
                                      (
                                          shared
                                      ) => {
                                          if (
                                              typeof shared !==
                                              "string"
                                          ) {
                                              return (
                                                  shared.name ||
                                                  "Unknown"
                                              );
                                          }

                                          const participant =
                                              trip.participants.find(
                                                  (
                                                      candidate
                                                  ) =>
                                                      candidate.id ===
                                                      shared
                                              );

                                          return (
                                              participant?.name ??
                                              "Unknown"
                                          );
                                      }
                                  )
                                  .join(
                                      ", "
                                  )
                            : "None";

                    return {
                        ...expense,
                        paidByName:
                            expense
                                .participant
                                ?.name ??
                            "Unknown",
                        sharedByNames,
                    };
                }
            );
        }, [trip]);

    /*
     * Settlements are calculated locally from the
     * loaded expenses so each currency remains
     * completely independent.
     */
    const settlements =
        useMemo(() => {
            if (!trip) {
                return [];
            }

            return calculateSettlements(
                trip
            );
        }, [trip]);

    /*
     * Calculate total expenses independently
     * for every currency.
     */
    const expenseTotalsByCurrency =
        useMemo(() => {
            if (!trip) {
                return [];
            }

            const totals =
                new Map<
                    string,
                    number
                >();

            for (const expense of trip.expenses) {
                const currency =
                    expense.currency
                        ?.trim()
                        .toUpperCase() ||
                    "CAD";

                totals.set(
                    currency,
                    (totals.get(
                        currency
                    ) ?? 0) +
                        Number(
                            expense.amount ||
                                0
                        )
                );
            }

            return Array.from(
                totals.entries()
            )
                .sort(
                    (
                        [
                            currencyA,
                        ],
                        [
                            currencyB,
                        ]
                    ) =>
                        currencyA.localeCompare(
                            currencyB
                        )
                )
                .map(
                    ([
                        currency,
                        amount,
                    ]) => ({
                        currency,
                        amount,
                    })
                );
        }, [trip]);

    const doneParticipantCount =
        trip?.participants.filter(
            (participant) =>
                participant.participantDone
        ).length ?? 0;

    const pendingParticipantCount =
        (trip?.participants.length ??
            0) -
        doneParticipantCount;

    if (isLoading) {
        return (
            <div className="trip-details-page container py-4">
                <div
                    className="trip-loading-panel"
                    role="status"
                    aria-live="polite"
                >
                    <div
                        className="spinner-border text-primary"
                        aria-hidden="true"
                    />
                    <span>
                        Loading trip details...
                    </span>
                </div>
            </div>
        );
    }

    if (!trip) {
        return (
            <div className="trip-details-page container py-4">
                <Link
                    to="/"
                    className="trip-back-link"
                >
                    <span aria-hidden="true">
                        &larr;
                    </span>
                    Back to trips
                </Link>

                <div className="trip-empty-panel trip-empty-panel-error">
                    <div
                        className="trip-empty-icon"
                        aria-hidden="true"
                    >
                        !
                    </div>

                    <div>
                        <h2>
                            Unable to load this
                            trip
                        </h2>

                        <p>
                            {pageError ||
                                "Trip not found."}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="trip-details-page container py-4">
            <Link
                to="/"
                className="trip-back-link"
            >
                <span aria-hidden="true">
                    &larr;
                </span>
                Back to trips
            </Link>

           <section className="trip-header-panel">
    <div className="trip-header-copy">
        <span className="trip-kicker">
            Trip workspace
        </span>

        <h1>{trip.name}</h1>

        <p>
            Add expenses, track participant progress,
            and review the payments needed to settle the
            trip.
        </p>
    </div>

    <div
        className="trip-header-metrics"
        aria-label="Trip summary"
    >
        <div className="trip-metric">
            <span className="trip-metric-label">
                Expenses
            </span>

            <strong className="trip-metric-multi-value">
                {expenseTotalsByCurrency.length > 0
                    ? expenseTotalsByCurrency.map(
                          ({ currency, amount }) => (
                              <span
                                  key={currency}
                                  style={{
                                      display: "block",
                                      marginBottom: "3px",
                                  }}
                              >
                                  <span
                                      style={{
                                          fontSize: "0.7em",
                                          opacity: 0.65,
                                          marginRight: "5px",
                                      }}
                                  >
                                      {currency}
                                  </span>
                                  {formatAmount(
                                      amount,
                                      currency
                                  )}
                              </span>
                          )
                      )
                    : (
                        <span>
                            <span
                                style={{
                                    fontSize: "0.7em",
                                    opacity: 0.65,
                                    marginRight: "5px",
                                }}
                            >
                                CAD
                            </span>
                            {formatAmount(0, "CAD")}
                        </span>
                    )}
            </strong>
        </div>

        <div className="trip-metric">
            <span className="trip-metric-label">
                Participants
            </span>

            <strong>
                {trip.participants.length}
            </strong>
        </div>

        <div className="trip-metric">
            <span className="trip-metric-label">
                Done
            </span>

            <strong>
                {doneParticipantCount}/
                {trip.participants.length}
            </strong>
        </div>
    </div>
</section>
            <section
                className="trip-action-bar"
                aria-label="Trip actions"
            >
                <div
                    className="trip-action-secondary"
                    style={{
                        marginLeft:
                            "auto",
                    }}
                >
                    <button
                        type="button"
                        className="trip-action-button trip-action-button-neutral"
                        onClick={
                            notifyAllParticipants
                        }
                        disabled={
                            notifyLoading
                        }
                    >
                        {notifyLoading
                            ? "Sending..."
                            : "Notify all"}
                    </button>

                    <button
                        type="button"
                        className={
                            myParticipant?.participantDone
                                ? "trip-action-button trip-action-button-success"
                                : "trip-action-button trip-action-button-neutral"
                        }
                        onClick={
                            markParticipantDone
                        }
                        disabled={
                            marking ||
                            !myParticipant
                        }
                        title={
                            myParticipant
                                ? myParticipant.participantDone
                                    ? "Unmark yourself as done"
                                    : "Mark yourself as done"
                                : "Your login email is not a participant on this trip"
                        }
                    >
                        {marking
                            ? "Updating..."
                            : myParticipant?.participantDone
                            ? "Marked as done"
                            : "Mark myself done"}
                    </button>

                    <button
                        type="button"
                        className="trip-action-button trip-action-button-dark"
                        onClick={
                            sendTripByEmail
                        }
                        disabled={emailing}
                    >
                        {emailing
                            ? "Sending..."
                            : "Email trip summary"}
                    </button>
                </div>
            </section>

            <div
                className="trip-message-stack"
                aria-live="polite"
            >
                {markError && (
                    <div className="trip-message trip-message-error">
                        {markError}
                    </div>
                )}

                {markStatusMessage && (
                    <div className="trip-message trip-message-success">
                        {
                            markStatusMessage
                        }
                    </div>
                )}

                {emailError && (
                    <div className="trip-message trip-message-error">
                        {emailError}
                    </div>
                )}

                {emailResult && (
                    <div className="trip-message trip-message-info">
                        {emailResult}
                    </div>
                )}
            </div>

            <div className="trip-overview-grid">
                <TripSummaryCard
                    trip={trip}
                />

                <SettlementSummaryCard
                    settlements={
                        settlements
                    }
                />
            </div>

            <div className="trip-content-stack">
                <section className="trip-panel trip-expenses-panel">
                    <div className="trip-panel-header">
                       <div>
    <span className="trip-panel-eyebrow">
        Spending
    </span>

    <h2>
        Expenses
    </h2>

    <p>
        {expensesTotal ??
            trip.expenses.length}{" "}
        recorded expense
        {(expensesTotal ?? trip.expenses.length) === 1
            ? ""
            : "s"}
    </p>

    <div
        style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            marginTop: "12px",
        }}
    >
        {(() => {
            const totalsByCurrency = trip.expenses.reduce(
                (totals, expense) => {
                    const currency =
                        expense.currency?.toUpperCase() || "CAD";

                    totals[currency] =
                        (totals[currency] || 0) +
                        Number(expense.amount || 0);

                    return totals;
                },
                {} as Record<string, number>
            );

            return Object.entries(totalsByCurrency).map(
                ([currency, total]) => {
                    const formatted =
                        currency === "USD"
                            ? `US$${total.toFixed(2)}`
                            : currency === "CAD"
                              ? `$${total.toFixed(2)}`
                              : `${currency} ${total.toFixed(2)}`;

                    return (
                        <div
                            key={currency}
                            style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                background: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <strong>{currency}</strong>
                            <span>{formatted}</span>
                        </div>
                    );
                }
            );
        })()}
    </div>
</div>

                        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                           

                            <button
                                type="button"
                                className="trip-action-button trip-action-button-primary"
                                onClick={
                                    openAddExpenseModal
                                }
                            >
                                <span
                                    className="trip-action-icon"
                                    aria-hidden="true"
                                >
                                    +
                                </span>

                                Add expense
                            </button>
                        </div>
                    </div>

                    <div className="trip-panel-body">
                        <div className="d-none d-md-block">
                            {expensesWithNames.length >
                            0 ? (
                                <>
                                    <div className="trip-table-wrap">
                                        <table className="trip-expense-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">
                                                        Expense
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="trip-align-right"
                                                    >
                                                        Amount
                                                    </th>

                                                    <th scope="col">
                                                        Paid by
                                                    </th>

                                                    <th scope="col">
                                                        Shared by
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="trip-action-column"
                                                    >
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {expensesWithNames.map(
                                                    (
                                                        expense
                                                    ) => (
                                                        <tr
                                                            key={
                                                                expense.id
                                                            }
                                                        >
                                                            <td>
                                                                <div className="trip-expense-cell">
                                                                    <span
                                                                        className="trip-expense-icon"
                                                                        aria-hidden="true"
                                                                    >
                                                                        {expense.description
                                                                            .trim()
                                                                            .charAt(
                                                                                0
                                                                            )
                                                                            .toUpperCase() ||
                                                                            "$"}
                                                                    </span>

                                                                    <div>
                                                                        <strong>
                                                                            {expense.description ||
                                                                                "No description"}
                                                                        </strong>

                                                                        <small>
                                                                            {
                                                                                expense.currency
                                                                            }{" "}
                                                                            ·
                                                                            Shared
                                                                            trip
                                                                            expense
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className="trip-align-right">
                                                                <strong className="trip-amount">
                                                                    {formatAmount(
                                                                        Number(
                                                                            expense.amount
                                                                        ),
                                                                        expense.currency ||
                                                                            "CAD"
                                                                    )}
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                <div className="trip-person-cell">
                                                                    <span className="trip-mini-avatar">
                                                                        {getInitials(
                                                                            expense.paidByName,
                                                                            ""
                                                                        )}
                                                                    </span>

                                                                    <span>
                                                                        {
                                                                            expense.paidByName
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="trip-chip-list">
                                                                    {expense.sharedByNames
                                                                        .split(
                                                                            ","
                                                                        )
                                                                        .map(
                                                                            (
                                                                                name
                                                                            ) =>
                                                                                name.trim()
                                                                        )
                                                                        .filter(
                                                                            Boolean
                                                                        )
                                                                        .map(
                                                                            (
                                                                                name,
                                                                                index
                                                                            ) => (
                                                                                <span
                                                                                    key={`${expense.id}-${name}-${index}`}
                                                                                    className="trip-chip"
                                                                                >
                                                                                    {
                                                                                        name
                                                                                    }
                                                                                </span>
                                                                            )
                                                                        )}
                                                                </div>
                                                            </td>

                                                            <td className="trip-action-column">
                                                                <button
                                                                    type="button"
                                                                    className="trip-row-action trip-row-action-danger"
                                                                    onClick={() =>
                                                                        handleDeleteExpense(
                                                                            expense.id
                                                                        )
                                                                    }
                                                                    aria-label={`Delete ${
                                                                        expense.description ||
                                                                        "expense"
                                                                    }`}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {expensesTotal !==
                                        null &&
                                        trip.expenses
                                            .length <
                                            expensesTotal && (
                                            <div className="trip-load-more">
                                                <button
                                                    type="button"
                                                    className="trip-action-button trip-action-button-neutral"
                                                    onClick={
                                                        loadMoreExpenses
                                                    }
                                                    disabled={
                                                        loadingExpenses
                                                    }
                                                >
                                                    {loadingExpenses
                                                        ? "Loading..."
                                                        : "Load more expenses"}
                                                </button>
                                            </div>
                                        )}
                                </>
                            ) : (
                                <div className="trip-empty-panel">
                                    <div
                                        className="trip-empty-icon"
                                        aria-hidden="true"
                                    >
                                        +
                                    </div>

                                    <div>
                                        <h3>
                                            No expenses yet
                                        </h3>

                                        <p>
                                            Add the first
                                            expense to start
                                            calculating the
                                            group balances.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="d-md-none">
                            {expensesWithNames.length >
                            0 ? (
                                <div className="trip-mobile-list">
                                    {expensesWithNames.map(
                                        (
                                            expense
                                        ) => (
                                            <article
                                                key={
                                                    expense.id
                                                }
                                                className="trip-mobile-expense"
                                            >
                                                <div className="trip-mobile-expense-header">
                                                    <div className="trip-expense-cell">
                                                        <span
                                                            className="trip-expense-icon"
                                                            aria-hidden="true"
                                                        >
                                                            {expense.description
                                                                .trim()
                                                                .charAt(
                                                                    0
                                                                )
                                                                .toUpperCase() ||
                                                                "$"}
                                                        </span>

                                                        <div>
                                                            <strong>
                                                                {expense.description ||
                                                                    "No description"}
                                                            </strong>

                                                            <small>
                                                                {
                                                                    expense.currency
                                                                }{" "}
                                                                · Paid
                                                                by{" "}
                                                                {
                                                                    expense.paidByName
                                                                }
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <strong className="trip-amount">
                                                        {formatAmount(
                                                            Number(
                                                                expense.amount
                                                            ),
                                                            expense.currency ||
                                                                "CAD"
                                                        )}
                                                    </strong>
                                                </div>

                                                <div className="trip-mobile-expense-shared">
                                                    <span>
                                                        Shared by
                                                    </span>

                                                    <div className="trip-chip-list">
                                                        {expense.sharedByNames
                                                            .split(
                                                                ","
                                                            )
                                                            .map(
                                                                (
                                                                    name
                                                                ) =>
                                                                    name.trim()
                                                            )
                                                            .filter(
                                                                Boolean
                                                            )
                                                            .map(
                                                                (
                                                                    name
                                                                ) => (
                                                                    <span
                                                                        key={`${expense.id}-mobile-${name}`}
                                                                        className="trip-chip"
                                                                    >
                                                                        {
                                                                            name
                                                                        }
                                                                    </span>
                                                                )
                                                            )}
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="trip-row-action trip-row-action-danger"
                                                    onClick={() =>
                                                        handleDeleteExpense(
                                                            expense.id
                                                        )
                                                    }
                                                >
                                                    Delete expense
                                                </button>
                                            </article>
                                        )
                                    )}

                                    {expensesTotal !==
                                        null &&
                                        trip.expenses
                                            .length <
                                            expensesTotal && (
                                            <button
                                                type="button"
                                                className="trip-action-button trip-action-button-neutral trip-full-width"
                                                onClick={
                                                    loadMoreExpenses
                                                }
                                                disabled={
                                                    loadingExpenses
                                                }
                                            >
                                                {loadingExpenses
                                                    ? "Loading..."
                                                    : "Load more expenses"}
                                            </button>
                                        )}
                                </div>
                            ) : (
                                <div className="trip-empty-panel">
                                    <div
                                        className="trip-empty-icon"
                                        aria-hidden="true"
                                    >
                                        +
                                    </div>

                                    <div>
                                        <h3>
                                            No expenses yet
                                        </h3>

                                        <p>
                                            Add the first
                                            expense to begin.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="trip-panel trip-participants-panel">
                    <div className="trip-panel-header">
                        <div>
                            <span className="trip-panel-eyebrow">
                                Group progress
                            </span>

                            <h2>
                                Participants
                            </h2>

                            <p>
                                {
                                    doneParticipantCount
                                }{" "}
                                done ·{" "}
                                {
                                    pendingParticipantCount
                                }{" "}
                                pending
                            </p>
                        </div>

                        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                            <span
                                className={
                                    pendingParticipantCount ===
                                        0 &&
                                    trip
                                        .participants
                                        .length >
                                        0
                                        ? "trip-progress-badge trip-progress-badge-done"
                                        : "trip-progress-badge"
                                }
                            >
                                {pendingParticipantCount ===
                                    0 &&
                                trip
                                    .participants
                                    .length >
                                    0
                                    ? "Everyone is done"
                                    : `${pendingParticipantCount} pending`}
                            </span>

                            <button
                                type="button"
                                className="trip-action-button trip-action-button-primary-soft"
                                onClick={
                                    openAddParticipantModal
                                }
                            >
                                <span
                                    className="trip-action-icon"
                                    aria-hidden="true"
                                >
                                    +
                                </span>

                                Add participant
                            </button>
                        </div>
                    </div>

                    <div className="trip-panel-body">
                        {trip.participants
                            .length > 0 ? (
                            <>
                                <div className="d-none d-md-block trip-table-wrap">
                                    <table className="trip-expense-table trip-participant-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">
                                                    Participant
                                                </th>

                                                <th scope="col">
                                                    Email
                                                </th>

                                                <th scope="col">
                                                    Status
                                                </th>

                                                <th
                                                    scope="col"
                                                    className="trip-action-column"
                                                >
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {trip.participants.map(
                                                (
                                                    participant
                                                ) => (
                                                    <tr
                                                        key={
                                                            participant.id
                                                        }
                                                    >
                                                        <td>
                                                            <div className="trip-participant-identity">
                                                                <span className="trip-participant-avatar">
                                                                    {getInitials(
                                                                        participant.name,
                                                                        participant.email
                                                                    )}
                                                                </span>

                                                                <strong>
                                                                    {participant.name ||
                                                                        "Unnamed participant"}
                                                                </strong>
                                                            </div>
                                                        </td>

                                                        <td className="trip-participant-email">
                                                            {
                                                                participant.email
                                                            }
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={
                                                                    participant.participantDone
                                                                        ? "trip-status-badge trip-status-done"
                                                                        : "trip-status-badge trip-status-pending"
                                                                }
                                                            >
                                                                <span
                                                                    className="trip-status-dot"
                                                                    aria-hidden="true"
                                                                />

                                                                {participant.participantDone
                                                                    ? "Done"
                                                                    : "Pending"}
                                                            </span>
                                                        </td>

                                                        <td className="trip-action-column">
                                                            <div className="trip-table-actions">
                                                                <button
                                                                    type="button"
                                                                    className="trip-row-action"
                                                                    onClick={() =>
                                                                        notifyParticipant(
                                                                            participant
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        notifyLoading
                                                                    }
                                                                >
                                                                    Notify
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="trip-row-action trip-row-action-danger"
                                                                    onClick={() =>
                                                                        handleDeleteParticipant(
                                                                            participant.id
                                                                        )
                                                                    }
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-md-none trip-participant-list">
                                    {trip.participants.map(
                                        (
                                            participant
                                        ) => (
                                            <article
                                                key={
                                                    participant.id
                                                }
                                                className="trip-participant-row"
                                            >
                                                <div className="trip-participant-identity">
                                                    <span className="trip-participant-avatar">
                                                        {getInitials(
                                                            participant.name,
                                                            participant.email
                                                        )}
                                                    </span>

                                                    <div className="trip-participant-copy">
                                                        <strong>
                                                            {participant.name ||
                                                                "Unnamed participant"}
                                                        </strong>

                                                        <span>
                                                            {
                                                                participant.email
                                                            }
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="trip-participant-status">
                                                    <span
                                                        className={
                                                            participant.participantDone
                                                                ? "trip-status-badge trip-status-done"
                                                                : "trip-status-badge trip-status-pending"
                                                        }
                                                    >
                                                        <span
                                                            className="trip-status-dot"
                                                            aria-hidden="true"
                                                        />

                                                        {participant.participantDone
                                                            ? "Done"
                                                            : "Pending"}
                                                    </span>
                                                </div>

                                                <div className="trip-participant-actions">
                                                    <button
                                                        type="button"
                                                        className="trip-row-action"
                                                        onClick={() =>
                                                            notifyParticipant(
                                                                participant
                                                            )
                                                        }
                                                        disabled={
                                                            notifyLoading
                                                        }
                                                    >
                                                        Notify
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="trip-row-action trip-row-action-danger"
                                                        onClick={() =>
                                                            handleDeleteParticipant(
                                                                participant.id
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </article>
                                        )
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="trip-empty-panel">
                                <div
                                    className="trip-empty-icon"
                                    aria-hidden="true"
                                >
                                    +
                                </div>

                                <div>
                                    <h3>
                                        No participants yet
                                    </h3>

                                    <p>
                                        Add your friends
                                        so they can enter
                                        expenses.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {showAddParticipant && (
                <div
                    className="modal fade show d-block trip-modal-backdrop"
                    tabIndex={-1}
                    onClick={
                        closeAddParticipantModal
                    }
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className="modal-dialog modal-dialog-centered trip-modal-dialog"
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                    >
                        <div className="modal-content trip-modal-content">
                            <div className="modal-header trip-modal-header">
                                <h5 className="modal-title">
                                    Add Participant
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={
                                        closeAddParticipantModal
                                    }
                                    aria-label="Close"
                                />
                            </div>

                            <form
                                onSubmit={
                                    handleAddParticipant
                                }
                            >
                                <div className="modal-body trip-modal-body">
                                    {participantErrorMsg && (
                                        <div className="alert alert-danger">
                                            {
                                                participantErrorMsg
                                            }
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor="participant-name"
                                        >
                                            Name
                                        </label>

                                        <input
                                            id="participant-name"
                                            className="form-control trip-form-control"
                                            value={
                                                participantName
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                handleParticipantNameSearchChange(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            disabled={
                                                addingParticipant
                                            }
                                            autoFocus
                                        />
                                    </div>

                                    <div className="mb-3 position-relative">
                                        <label
                                            className="form-label"
                                            htmlFor="participant-email"
                                        >
                                            Email
                                        </label>

                                        <input
                                            id="participant-email"
                                            type="email"
                                            className="form-control trip-form-control"
                                            value={
                                                participantEmail
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                handleParticipantSearchChange(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            disabled={
                                                addingParticipant
                                            }
                                        />

                                        {contactSearchLoading && (
                                            <div className="form-text">
                                                Searching
                                                contacts...
                                            </div>
                                        )}

                                        {contactSuggestions.length >
                                            0 && (
                                            <div
                                                className="list-group position-absolute w-100"
                                                style={{
                                                    zIndex: 1050,
                                                }}
                                            >
                                                {contactSuggestions.map(
                                                    (
                                                        contact
                                                    ) => (
                                                        <button
                                                            key={
                                                                contact.id
                                                            }
                                                            type="button"
                                                            className="list-group-item list-group-item-action"
                                                            onClick={() => {
                                                                setParticipantName(
                                                                    contact.name
                                                                );

                                                                setParticipantEmail(
                                                                    contact.email
                                                                );

                                                                setContactSuggestions(
                                                                    []
                                                                );
                                                            }}
                                                        >
                                                            <strong>
                                                                {
                                                                    contact.name
                                                                }
                                                            </strong>{" "}
                                                            <small className="text-muted">
                                                                {
                                                                    contact.email
                                                                }
                                                            </small>
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}

                                        <div className="trip-form-note mt-3">
                                            Add a
                                            participant's
                                            email to give
                                            them access when
                                            they sign in.
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer trip-modal-footer">
                                    <button
                                        type="button"
                                        className="trip-action-button trip-action-button-neutral"
                                        onClick={
                                            closeAddParticipantModal
                                        }
                                        disabled={
                                            addingParticipant
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="trip-action-button trip-action-button-primary"
                                        disabled={
                                            addingParticipant
                                        }
                                    >
                                        {addingParticipant
                                            ? "Adding..."
                                            : "Add Participant"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showAddExpense && (
                <div
                    className="modal fade show d-block trip-modal-backdrop"
                    tabIndex={-1}
                    onClick={
                        closeAddExpenseModal
                    }
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className="modal-dialog modal-dialog-centered trip-modal-dialog"
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                    >
                        <div className="modal-content trip-modal-content">
                            <div className="modal-header trip-modal-header">
                                <h5 className="modal-title">
                                    Add Expense
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={
                                        closeAddExpenseModal
                                    }
                                    aria-label="Close"
                                />
                            </div>

                            <form
                                onSubmit={
                                    handleAddExpense
                                }
                            >
                                <div className="modal-body trip-modal-body">
                                    {expenseError && (
                                        <div className="alert alert-danger">
                                            {
                                                expenseError
                                            }
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor="expense-description"
                                        >
                                            Description
                                        </label>

                                        <div className="position-relative">
                                            <input
                                                id="expense-description"
                                                className="form-control trip-form-control"
                                                value={
                                                    expenseDescription
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    handleExpenseDescriptionChange(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                disabled={
                                                    addingExpense
                                                }
                                                autoFocus
                                                autoComplete="off"
                                            />

                                            {itemSuggestions.length >
                                                0 && (
                                                <div
                                                    className="list-group position-absolute w-100"
                                                    style={{
                                                        zIndex: 1050,
                                                    }}
                                                >
                                                    {itemSuggestions.map(
                                                        (
                                                            item: any
                                                        ) => (
                                                            <button
                                                                key={String(
                                                                    item.id ??
                                                                        item.Id ??
                                                                        item.name
                                                                )}
                                                                type="button"
                                                                className="list-group-item list-group-item-action"
                                                                onClick={() => {
                                                                    setExpenseDescription(
                                                                        String(
                                                                            item.name ??
                                                                                item.Name ??
                                                                                ""
                                                                        )
                                                                    );

                                                                    setExpenseAmount(
                                                                        Number(
                                                                            item.defaultAmount ??
                                                                                item.DefaultAmount ??
                                                                                0
                                                                        )
                                                                    );

                                                                    setExpenseCurrency(
                                                                        String(
                                                                            item.currency ??
                                                                                item.Currency ??
                                                                                "CAD"
                                                                        ).toUpperCase()
                                                                    );

                                                                    setItemSuggestions(
                                                                        []
                                                                    );
                                                                }}
                                                            >
                                                                <strong>
                                                                    {item.name ??
                                                                        item.Name}
                                                                </strong>{" "}
                                                                <small className="text-muted">
                                                                    {item.defaultAmount !=
                                                                        null ||
                                                                    item.DefaultAmount !=
                                                                        null
                                                                        ? `${item.currency ?? item.Currency ?? "CAD"} ${
                                                                              item.defaultAmount ??
                                                                              item.DefaultAmount
                                                                          }`
                                                                        : item.currency ??
                                                                          item.Currency ??
                                                                          "CAD"}
                                                                </small>
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor="expense-amount"
                                        >
                                            Amount
                                        </label>

<input
    id="expense-amount"
    type="number"
    className="form-control trip-form-control"
    value={expenseAmount === 0 ? "" : expenseAmount}
    onChange={(event) => {
        const value = event.target.value;

        if (value === "") {
            setExpenseAmount(0);
            return;
        }

        setExpenseAmount(Number(value));
    }}
    min="0.01"
    step="0.01"
    disabled={addingExpense}
/>
                                    </div>

                                    <div className="mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor="expense-currency"
                                        >
                                            Currency
                                        </label>

                                        <input
                                            id="expense-currency"
                                            className="form-control trip-form-control"
                                            value={
                                                expenseCurrency
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setExpenseCurrency(
                                                    event
                                                        .target
                                                        .value
                                                        .toUpperCase()
                                                        .slice(
                                                            0,
                                                            3
                                                        )
                                                )
                                            }
                                            minLength={
                                                3
                                            }
                                            maxLength={
                                                3
                                            }
                                            disabled={
                                                addingExpense
                                            }
                                            list="expense-currency-options"
                                        />

                                        <datalist id="expense-currency-options">
                                            <option value="CAD" />
                                            <option value="USD" />
                                            <option value="EUR" />
                                            <option value="GBP" />
                                            <option value="TRY" />
                                            <option value="JPY" />
                                            <option value="AUD" />
                                            <option value="MXN" />
                                        </datalist>
                                    </div>

                                    <div className="mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor="expense-paid-by"
                                        >
                                            Paid By
                                        </label>

                                        <select
                                            id="expense-paid-by"
                                            className="form-select trip-form-control"
                                            value={
                                                paidByParticipantId
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setPaidByParticipantId(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            disabled={
                                                addingExpense
                                            }
                                        >
                                            <option value="">
                                                Select
                                                participant
                                            </option>

                                            {trip.participants.map(
                                                (
                                                    participant
                                                ) => (
                                                    <option
                                                        key={
                                                            participant.id
                                                        }
                                                        value={
                                                            participant.id
                                                        }
                                                    >
                                                        {
                                                            participant.name
                                                        }
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    <fieldset className="mb-3">
                                        <legend className="form-label">
                                            Shared By
                                        </legend>

                                        <div className="d-flex flex-wrap gap-2">
                                            {trip.participants.map(
                                                (
                                                    participant
                                                ) => (
                                                    <div
                                                        key={
                                                            participant.id
                                                        }
                                                        className="form-check form-check-inline"
                                                    >
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`sharedBy-${participant.id}`}
                                                            checked={sharedByParticipantIds.includes(
                                                                participant.id
                                                            )}
                                                            onChange={() =>
                                                                toggleSharedBy(
                                                                    participant.id
                                                                )
                                                            }
                                                            disabled={
                                                                addingExpense
                                                            }
                                                        />

                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`sharedBy-${participant.id}`}
                                                        >
                                                            {
                                                                participant.name
                                                            }
                                                        </label>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </fieldset>
                                </div>

                                <div className="modal-footer trip-modal-footer">
                                    <button
                                        type="button"
                                        className="trip-action-button trip-action-button-neutral"
                                        onClick={
                                            closeAddExpenseModal
                                        }
                                        disabled={
                                            addingExpense
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="trip-action-button trip-action-button-primary"
                                        disabled={
                                            addingExpense
                                        }
                                    >
                                        {addingExpense
                                            ? "Adding..."
                                            : "Add Expense"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TripDetails;